import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { MediaAsset, Workspace } from '@/types';

interface ReelsDB extends DBSchema {
  media: {
    key: string;
    value: { asset: MediaAsset; blob: Blob };
  };
  workspace: {
    key: string;
    value: Workspace;
  };
}

const DB_NAME = 'reels-generator';
const DB_VERSION = 1;

let dbPromise: Promise<IDBPDatabase<ReelsDB>> | null = null;

function getDb(): Promise<IDBPDatabase<ReelsDB>> {
  if (!dbPromise) {
    dbPromise = openDB<ReelsDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains('media')) {
          db.createObjectStore('media');
        }
        if (!db.objectStoreNames.contains('workspace')) {
          db.createObjectStore('workspace');
        }
      },
    });
  }
  return dbPromise;
}

const WORKSPACE_KEY = 'current';

export async function saveWorkspace(workspace: Workspace): Promise<void> {
  const db = await getDb();
  await db.put('workspace', workspace, WORKSPACE_KEY);
}

export async function loadWorkspace(): Promise<Workspace | undefined> {
  const db = await getDb();
  return db.get('workspace', WORKSPACE_KEY);
}

export async function clearWorkspace(): Promise<void> {
  const db = await getDb();
  await db.delete('workspace', WORKSPACE_KEY);
}

export async function putMedia(asset: MediaAsset, blob: Blob): Promise<void> {
  const db = await getDb();
  await db.put('media', { asset, blob }, asset.id);
}

export async function getMedia(id: string): Promise<{ asset: MediaAsset; blob: Blob } | undefined> {
  const db = await getDb();
  return db.get('media', id);
}

export async function deleteMedia(id: string): Promise<void> {
  const db = await getDb();
  await db.delete('media', id);
}

export async function listMediaIds(): Promise<string[]> {
  const db = await getDb();
  return db.getAllKeys('media') as Promise<string[]>;
}

/** Removes any stored media blobs whose id is not present in `keepIds`. */
export async function pruneMedia(keepIds: Set<string>): Promise<number> {
  const db = await getDb();
  const allKeys = (await db.getAllKeys('media')) as string[];
  const tx = db.transaction('media', 'readwrite');
  let removed = 0;
  await Promise.all(
    allKeys.map(async (key) => {
      if (!keepIds.has(key)) {
        await tx.store.delete(key);
        removed += 1;
      }
    })
  );
  await tx.done;
  return removed;
}

/** Best-effort estimate of how much storage is used vs. available, for memory warnings. */
export async function estimateStorage(): Promise<{ usage: number; quota: number } | null> {
  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return null;
  const estimate = await navigator.storage.estimate();
  return { usage: estimate.usage ?? 0, quota: estimate.quota ?? 0 };
}
